import { flatMap } from "lodash";

import { hasSenderReceiverCountries } from "../../../common/update/utils/hasSenderReceiverCountries";
import { transformCreateBookingFormStateToMinimalBooking } from "../../../common/utils/minimalBooking";
import { ImportExportType } from "../../../gql/graphql";
import type {
  CreateCandidate,
  CreateFormState,
} from "../../../services/create/createFormReducer";
import {
  getImportExportSettingsFromMinimalBooking,
  getImportExportSettingsFromRoute,
} from "../../import-export/util/ImportExportSettingsGetters";

export const getCreateCandidateFormFields = (
  candidate: CreateCandidate,
  createFormState: CreateFormState,
): Set<SpecificFieldId> => {
  const minimalBooking = transformCreateBookingFormStateToMinimalBooking(
    createFormState,
    candidate,
  );

  const { vehicleType, route } = minimalBooking;

  const isCash = Boolean(createFormState.customer?.isCashCustomer);

  const columns = new Set<SpecificFieldId>();

  columns.add(SpecificFieldId.VehicleTypeCode);
  columns.add(SpecificFieldId.Length);
  columns.add(SpecificFieldId.Width);
  columns.add(SpecificFieldId.Height);

  if (
    getImportExportSettingsFromMinimalBooking(
      minimalBooking,
      getImportExportSettingsFromRoute(minimalBooking.route, "importReference"),
    )?.type === ImportExportType.EnvelopeId
  ) {
    columns.add(SpecificFieldId.ImportReference);
  }

  if (
    getImportExportSettingsFromMinimalBooking(
      minimalBooking,
      getImportExportSettingsFromRoute(minimalBooking.route, "exportReference"),
    )?.type === ImportExportType.EnvelopeId
  ) {
    columns.add(SpecificFieldId.ExportReference);
  }

  if (vehicleType?.useTrailerReg) {
    columns.add(SpecificFieldId.TrailerRegNo);
  }

  if (vehicleType?.useVehicleReg) {
    columns.add(SpecificFieldId.VehicleRegNo);
  }

  if (vehicleType?.useTradeWeight) {
    columns.add(SpecificFieldId.TradeWeight);
  }

  if (vehicleType?.useCargoWeight) {
    if (isCash) {
      columns.add(SpecificFieldId.GoodsWeight);
      columns.add(SpecificFieldId.GoodsType);
      columns.add(SpecificFieldId.GoodsDescription);

      if (
        route?.useSenderReceiverCountry &&
        hasSenderReceiverCountries(minimalBooking)
      ) {
        columns.add(SpecificFieldId.ReceiverCountryCode);
        columns.add(SpecificFieldId.SenderCountryCode);
      }
    } else if (route?.useCargoWeight) {
      columns.add(SpecificFieldId.GoodsWeight);
      columns.add(SpecificFieldId.GoodsType);
      columns.add(SpecificFieldId.GoodsDescription);
    }
  }

  if (vehicleType?.useNoOfDrivers) {
    columns.add(SpecificFieldId.NoOfDrivers);
  }

  if (vehicleType?.useHazardous && route?.useHazardousGoods) {
    columns.add(SpecificFieldId.HazardousGoods);
  }

  if (vehicleType?.useAnimals && route?.useLivestockType) {
    columns.add(SpecificFieldId.LivestockTypeCode);
  }

  if (vehicleType?.useTemperature) {
    columns.add(SpecificFieldId.Temperature);
  }

  columns.add(SpecificFieldId.CustomerReference);
  columns.add(SpecificFieldId.LoadingPreferenceCode);
  columns.add(SpecificFieldId.LoadingListMessage);

  if (route?.useCustomsClassification) {
    columns.add(SpecificFieldId.CustomsClassification);
  }

  return columns;
};

export const getCreateFormFieldsToShow = (
  state: CreateFormState,
): Set<SpecificFieldId> => {
  const createFieldCollection = flatMap(state.bookings, (booking) => [
    ...getCreateCandidateFormFields(booking, state),
  ]);

  return new Set(createFieldCollection);
};

export enum SpecificFieldId {
  GoodsWeight = "GoodsWeight",
  CustomerReference = "CustomerReference",
  CustomsClassification = "CustomsClassification",
  ExportReference = "ExportReference",
  GoodsDescription = "GoodsDescription",
  GoodsType = "GoodsType",
  HazardousGoods = "HazardousGoods",
  Height = "Height",
  ImportReference = "ImportReference",
  Length = "Length",
  LivestockTypeCode = "LivestockTypeCode",
  LoadingPreferenceCode = "LoadingPreferenceCode",
  LoadingListMessage = "LoadingListMessage",
  NoOfDrivers = "NoOfDrivers",
  ReceiverCountryCode = "ReceiverCountryCode",
  SenderCountryCode = "SenderCountryCode",
  Temperature = "Temperature",
  TradeWeight = "TradeWeight",
  TrailerRegNo = "TrailerRegNo",
  VehicleRegNo = "VehicleRegNo",
  VehicleTypeCode = "VehicleTypeCode",
  Width = "Width",
}

export interface SpecificTableColumn {
  description?: string;
  id: SpecificFieldId;
  label: string;
  onlyShowInExtendedMode: boolean;
  width: number;
}

export const specificTableColumns: SpecificTableColumn[] = [
  {
    id: SpecificFieldId.VehicleRegNo,
    label: "Vehicle reg.",
    onlyShowInExtendedMode: false,
    width: 130,
    description: "Vehicle registration number",
  },
  {
    id: SpecificFieldId.TrailerRegNo,
    label: "Trailer reg.",
    onlyShowInExtendedMode: false,
    width: 130,
    description: "Trailer registration number",
  },
  {
    id: SpecificFieldId.CustomerReference,
    label: "Customer reference",
    onlyShowInExtendedMode: false,
    width: 160,
  },
  {
    id: SpecificFieldId.HazardousGoods,
    label: "Haz. goods",
    onlyShowInExtendedMode: false,
    width: 100,
    description: "Hazardous goods",
  },
  {
    id: SpecificFieldId.LivestockTypeCode,
    label: "Animals",
    onlyShowInExtendedMode: false,
    width: 135,
  },
  {
    id: SpecificFieldId.Temperature,
    label: "Temperature",
    onlyShowInExtendedMode: false,
    width: 110,
    description: "Temperature onboard (ºC)",
  },
  {
    id: SpecificFieldId.GoodsType,
    label: "Goods type",
    onlyShowInExtendedMode: false,
    width: 100,
    description: "Goods type",
  },
  {
    id: SpecificFieldId.GoodsDescription,
    label: "Goods description",
    onlyShowInExtendedMode: false,
    width: 320,
    description: "Goods description",
  },
  {
    id: SpecificFieldId.GoodsWeight,
    label: "Goods weight",
    onlyShowInExtendedMode: false,
    width: 100,
    description: "Goods weight (kg)",
  },
  {
    id: SpecificFieldId.LoadingPreferenceCode,
    label: "Loading preference",
    onlyShowInExtendedMode: false,
    width: 160,
  },
  {
    id: SpecificFieldId.NoOfDrivers,
    label: "Drivers",
    onlyShowInExtendedMode: false,
    width: 76,
  },
  {
    id: SpecificFieldId.CustomsClassification,
    label: "UK customs procedure",
    onlyShowInExtendedMode: false,
    width: 240,
    description: "UK customs procedure (NL-UK)",
  },
  {
    id: SpecificFieldId.SenderCountryCode,
    label: "Sender country",
    onlyShowInExtendedMode: false,
    width: 125,
  },
  {
    id: SpecificFieldId.ReceiverCountryCode,
    label: "Receiver country",
    onlyShowInExtendedMode: false,
    width: 125,
  },
  {
    id: SpecificFieldId.VehicleTypeCode,
    label: "Vehicle type",
    onlyShowInExtendedMode: true,
    width: 145,
  },
  {
    id: SpecificFieldId.Length,
    label: "Length",
    onlyShowInExtendedMode: true,
    width: 70,
    description: "Length (m)",
  },
  {
    id: SpecificFieldId.Width,
    label: "Width",
    onlyShowInExtendedMode: true,
    width: 70,
    description: "Width (m)",
  },
  {
    id: SpecificFieldId.Height,
    label: "Height",
    onlyShowInExtendedMode: true,
    width: 70,
    description: "Height (m)",
  },
  {
    id: SpecificFieldId.ExportReference,
    label: "Export (to/from UK)",
    onlyShowInExtendedMode: false,
    width: 170,
  },
  {
    id: SpecificFieldId.ImportReference,
    label: "Import (to/from UK)",
    onlyShowInExtendedMode: false,
    width: 170,
  },
  {
    id: SpecificFieldId.TradeWeight,
    label: "Trade weight",
    onlyShowInExtendedMode: false,
    width: 100,
    description: "Trade weight (kg)",
  },
  {
    id: SpecificFieldId.LoadingListMessage,
    label: "Loading note",
    onlyShowInExtendedMode: false,
    width: 320,
  },
];

export const getCreateColumnsToShow = (
  allColumns: SpecificTableColumn[],
  fieldsToShow: Set<SpecificFieldId>,
): SpecificTableColumn[] => {
  if (allColumns.length === 0 || fieldsToShow.size === 0) {
    return [];
  }

  return allColumns.filter((column) => fieldsToShow.has(column.id));
};
